import React, { useEffect, useState } from "react";
import {
    Table, TableBody, TableCell, TableContainer, TableHead,
    TableRow, Paper, Box, IconButton
} from "@mui/material";
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import NestedDropdown from "../Reusablecomponents/NestedDropdown";
import { DataVisualizationApi } from "../api";

// Utility to extract nested value from dot-notation key
const getNestedValue = (row, key) => {
    const [section, name] = key.split(".");
    const list = row[section];

    if (Array.isArray(list)) {
        const item = list.find(entry => entry.name === name);
        return item?.value ?? "";
    }

    return "";
};

const getUnit = (rowData, key) => {
    const [section, name] = key.split(".");
    const list = rowData?.[section];

    if (Array.isArray(list)) {
        const item = list.find(entry => entry.name === name);
        return item?.unit ?? "";
    }

    return "";
};

// Color by group
const groupColors = {
    climate: "#69a3bb1a",
    harvest: "#0778c21a",
    plant: "#01755c1a"
};

const TableView = ({ cultivationZoneId, site, greenhouse, crop }) => {
    const [parameterOptions, setParameterOptions] = useState({});
    const [weeklyData, setWeeklyData] = useState([]);
    const [selectedColumns, setSelectedColumns] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!cultivationZoneId) return;

        const fetchData = async () => {
            try {
                setLoading(true);
                const oneYearAgo = new Date();
                oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
                const now = new Date();
                const [paramsList, weekly] = await Promise.all([
                    DataVisualizationApi.fetchParametersList(cultivationZoneId),
                    DataVisualizationApi.fetchParametersData(cultivationZoneId, oneYearAgo, now)
                ]);
                // Format parameters to match label/key structure
                const structuredParams = {};
                Object.entries(paramsList).forEach(([group, keys]) => {
                    structuredParams[group] = keys.map((key) => ({
                        key: `${group}.${key}`,
                        label: key.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())
                    }));
                });
                setParameterOptions(structuredParams);

                // Set default selected columns (first 5 available)
                const allParams = Object.values(structuredParams).flat();
                setSelectedColumns(allParams.slice(0, 20));

                setWeeklyData(weekly);
            } catch (err) {
                console.error("Error fetching table data:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [cultivationZoneId]);

    // Normalize selected columns into groups
    const normalizedColumns = [];
    for (const [group, params] of Object.entries(parameterOptions)) {
        for (const param of params) {
            if (selectedColumns.some((sel) => sel.key === param.key)) {
                normalizedColumns.push({ ...param, group });
            }
        }
    }

    const groupedColumns = normalizedColumns.reduce((acc, col) => {
        if (!acc[col.group]) acc[col.group] = [];
        acc[col.group].push(col);
        return acc;
    }, {});

    const lastColumnKeys = new Set(
        Object.values(groupedColumns).map((cols) => cols[cols.length - 1].key)
    );

    const handleDownloadExcel = () => {
        const headers = [
            "CW",
            ...normalizedColumns.map(col => {
                const unit = getUnit(weeklyData[0], col.key);
                return unit ? `${col.label} (${unit})` : col.label;
            })
        ];

        const data = weeklyData.map(row => {
            const rowData = { CW: row.calendar_week };
            normalizedColumns.forEach(col => {
                const unit = getUnit(row, col.key);
                const labelWithUnit = unit ? `${col.label} (${unit})` : col.label;
                rowData[labelWithUnit] = getNestedValue(row, col.key);
            });
            return rowData;
        });


        const worksheet = XLSX.utils.json_to_sheet(data, { header: headers });
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Data");
        const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
        const fileData = new Blob([excelBuffer], { type: "application/octet-stream" });
        const now = new Date();
        const timestamp = now.toISOString().split('T')[0];
        const safe = str => (str || 'unknown').replace(/\s+/g, '_');
        const filename = `${timestamp}_${safe(site)}_${safe(greenhouse)}_${safe(crop)}.xlsx`;
        saveAs(fileData, filename);
    };

    if (loading || !weeklyData.length) {
        return <Box p={2}>Loading table data...</Box>;
    }

    return (
        <Box sx={{ width: "100%", pl: 0 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0 }}>
                <NestedDropdown
                    name="Select Columns"
                    selectedParams={selectedColumns}
                    setSelectedParams={setSelectedColumns}
                    maxSelections={12}
                    parameters={parameterOptions}
                />

                <IconButton
                    color="#717171"
                    onClick={handleDownloadExcel}
                    size="large"
                >
                    <FileDownloadIcon />
                </IconButton>
            </Box>

            <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: 3, overflow: "hidden", }}>
                <Table stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell
                                sx={{
                                    backgroundColor: "#F5F5F5",
                                    fontWeight: "bold",
                                    border: "none",
                                }}
                            />
                            {Object.entries(groupedColumns).map(([group, cols]) => (
                                <TableCell
                                    key={group}
                                    colSpan={cols.length}
                                    align="center"
                                    sx={{
                                        backgroundColor: groupColors[group],
                                        fontWeight: "bold",
                                        border: "none",
                                        color: "#333"
                                    }}
                                >
                                    {group}
                                </TableCell>
                            ))}
                        </TableRow>


                        <TableRow>
                            <TableCell
                                sx={{
                                    backgroundColor: "#F5F5F5",
                                    fontWeight: "bold",
                                    borderBottom: "2px solid #B5B5B5",
                                    borderRight: "1px solid #E0E0E0"
                                }}
                            >
                                CW
                            </TableCell>
                            {normalizedColumns.map((col, i) => (
                                <TableCell
                                    key={col.key}
                                    align="center"
                                    sx={{
                                        backgroundColor: groupColors[col.group],
                                        fontWeight: 500,
                                        borderBottom: "2px solid #B5B5B5",
                                        paddingY: 1
                                    }}
                                >
                                    <Box display="flex" flexDirection="column" alignItems="center">
                                        <span>{col.label}</span>
                                        <span >
                                            {getUnit(weeklyData[0], col.key) ? `(${getUnit(weeklyData[0], col.key)})` : ""}
                                        </span>
                                    </Box>
                                </TableCell>

                            ))}
                        </TableRow>


                    </TableHead>

                    <TableBody>
                        {[...weeklyData].sort((a, b) => b.calendar_week - a.calendar_week).map((row, idx) => (
                            <TableRow key={idx}>
                                <TableCell
                                    sx={{
                                        backgroundColor: "#F5F5F5",
                                        fontWeight: "bold",
                                        border: "none"
                                    }}
                                >
                                    {row.calendar_week}
                                </TableCell>
                                {normalizedColumns.map((col) => (
                                    <TableCell
                                        key={col.key}
                                        align="center"
                                        sx={{
                                            backgroundColor: groupColors[col.group],
                                            border: "none"
                                        }}
                                    >
                                        {getNestedValue(row, col.key)}
                                    </TableCell>
                                ))}
                            </TableRow>

                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

export default TableView;
